import axiosInstance from './AxiosConfig';

/**
 * Obtener todos los partidos de un torneo específico.
 *
 * @param {number|string} torneoId - ID del torneo.
 * @returns {Promise<Array>} - Lista de partidos asociados al torneo.
 * @throws {Error} - Lanza un error si la solicitud falla.
 */
export const fetchTournamentMatchesAPI = async (torneoId) => {
  try {
    const response = await axiosInstance.get(`/api/torneos/${torneoId}/partidos`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener los partidos del torneo:', error);
    throw error;
  }
};

/**
 * Obtener un partido específico por ID.
 *
 * @param {number|string} partidoId - ID del partido.
 * @returns {Promise<Object>} - Detalles del partido.
 * @throws {Error} - Lanza un error si la solicitud falla.
 */
export const fetchMatchByIdAPI = async (partidoId) => {
  try {
    const response = await axiosInstance.get(`/api/partidos/${partidoId}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener el partido:', error);
    throw error;
  }
};

/**
 * Crear un nuevo partido dentro de un torneo específico.
 *
 * @param {number|string} torneoId - ID del torneo.
 * @param {Object} partidoData - Datos del partido a crear.
 * @returns {Promise<Object>} - Respuesta del servidor con los datos del partido creado.
 * @throws {Error} - Lanza un error si la solicitud falla.
 */
export const createMatchAPI = async (torneoId, partidoData) => {
  try {
    const response = await axiosInstance.post(`/api/torneos/${torneoId}/partidos`, partidoData);
    return response.data;
  } catch (error) {
    console.error('Error al crear el partido:', error);
    throw error;
  }
};

/**
 * Limpiar el horario de un partido específico.
 *
 * @param {number|string} matchId - 
 * @returns {Promise<Object>} 
 * @throws {Error}
 */
export const clearMatchScheduleAPI = async (matchId) => {
  try {
    const response = await axiosInstance.put(`/api/partidos/limpiar/${matchId}`);
    return response.data;
  } catch (error) {
    console.error('Error clearing match schedule:', error);
    throw error;
  }
};


/**
 * Actualizar un partido existente por su ID.
 *
 * @param {number|string} partidoId - ID del partido a actualizar.
 * @param {Object} updateData - Datos a actualizar del partido (sets y resultados de sets).
 * @returns {Promise<Object>} - Respuesta del servidor con los datos del partido actualizado.
 * @throws {Error} - Lanza un error si la solicitud falla.
 */
export const updateMatchResultAPI = async (partidoId, updateData) => {
  try {
    // console.log("Datos enviados al backend:", updateData);
    const response = await axiosInstance.put(`/api/partidos/${partidoId}`, updateData);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar el partido:', error);
    throw error;
  }
};

/**
 * Eliminar un partido dentro de un torneo específico.
 *
 * @param {number|string} torneoId - ID del torneo.
 * @param {number|string} partidoId - ID del partido a eliminar.
 * @returns {Promise<void>}
 * @throws {Error} - Lanza un error si la solicitud falla.
 */
export const deleteMatchAPI = async (torneoId, partidoId) => {
  try {
    await axiosInstance.delete(`/api/torneos/${torneoId}/partidos/${partidoId}`);
  } catch (error) {
    console.error('Error al eliminar el partido:', error);
    throw error;
  }
};

/**
 * Borrar resultados de un partido (sin eliminar el partido).
 *
 * @param {number|string} partidoId
 * @returns {Promise<Object>} - { message, partido }
 */
export const deleteMatchResultsAPI = async (partidoId) => {
  try {
    const response = await axiosInstance.delete(`/api/partidos/${partidoId}/resultados`);
    return response.data;
  } catch (error) {
    console.error('Error al borrar resultados del partido:', error);
    throw error;
  }
};


/**
 * Actualizar partido a NO PRESENTACION.
 *
 * Inicializa los resultados en 0 y actualiza el estado a 'terminado'.
 *
 * @param {number|string} partidoId - ID del partido a actualizar.
 * @returns {Promise<Object>} - Respuesta del servidor con los datos del partido actualizado.
 * @throws {Error} - Lanza un error si la solicitud falla.
 */
export const setNoPresentacionAPI = async (datosNP) => {
  try {
    const response = await axiosInstance.put(`/api/partidos/no-presentacion/${datosNP.matchId}`, datosNP);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar partido a NO PRESENTACION:', error);
    throw error;
  }
};


/**
 * Obtener el id_torneo_categoria dado un id de torneo y un número de partido.
 *
 * @param {number|string} torneoId      - ID del torneo.
 * @param {number|string} numeroPartido - Número de partido dentro del torneo.
 * @returns {Promise<{ id_torneo_categoria: number }>}
 * @throws {Error}                      - Lanza un error si la solicitud falla.
 */
export const fetchMatchCategoryAPI = async (torneoId, numeroPartido) => {
  try {
    const response = await axiosInstance.get(
      `/api/partidos/categoria/${torneoId}/${numeroPartido}`
    );
    return response.data;
  } catch (error) {
    console.error('Error al obtener id_torneo_categoria:', error);
    throw error;
  }
};